"use client";

import * as React from "react";
import Link from "next/link";
import { ChevronRight, ListMusic } from "lucide-react";
import { EmptyState, Skeleton } from "@gerama/ui";
import { formatNumber } from "@gerama/utils";
import type { Playlist, Song, Video } from "@gerama/types";
import { browserCoreApi } from "@/lib/api";
import { InfiniteSentinel, useInfiniteList } from "@/features/discover/use-infinite-list";
import { CardGrid, MediaCard } from "@/features/discover/shelf";
import { artistPath } from "./artist-path";
import { SongList, SongListHeader, useSongView } from "@/features/discover/song-list-view";
import { VideoCard } from "@/features/discover/video-card";

export type ArtistSection = "songs" | "videos" | "featuring";

const TITLE: Record<ArtistSection, string> = {
  songs: "آهنگ‌ها",
  videos: "نماهنگ‌ها",
  featuring: "همراهی در پلی‌لیست‌ها",
};

/** The "see all" pages behind the artist page: every song, video or featuring playlist, infinite. */
export function ArtistSectionList({
  artistId,
  artistCode,
  artistName,
  section,
}: {
  artistId: number;
  artistCode?: string | null;
  artistName: string;
  section: ArtistSection;
}) {
  const [view, setView] = useSongView();
  const [search, setSearch] = React.useState("");

  const songs = useInfiniteList<Song>({
    queryKey: ["artists", artistId, "songs", search],
    fetchPage: (page, limit, signal) =>
      browserCoreApi.songs.list(
        { artistId, sort: "popular", page, limit, search: search.trim() || undefined },
        signal,
      ),
    enabled: section === "songs",
    limit: 30,
  });
  const videos = useInfiniteList<Video>({
    queryKey: ["artists", artistId, "videos"],
    fetchPage: (page, limit, signal) =>
      browserCoreApi.videos.list({ artistId, sort: "newest", page, limit }, signal),
    enabled: section === "videos",
    limit: 24,
  });
  const playlists = useInfiniteList<Playlist>({
    queryKey: ["artists", artistId, "featuring"],
    fetchPage: (page, limit, signal) =>
      browserCoreApi.artists.featuring(artistId, { page, limit }, signal),
    enabled: section === "featuring",
    limit: 30,
  });
  const active = { songs, videos, featuring: playlists }[section];

  return (
    <div className="space-y-6">
      <Link
        href={artistPath({ id: artistId, code: artistCode })}
        className="text-muted-foreground hover:text-foreground inline-flex items-center gap-1 text-sm"
      >
        <ChevronRight className="size-4" />
        {artistName}
      </Link>
      {section === "songs" ? (
        <SongListHeader
          title={`${TITLE[section]} ${artistName}`}
          view={view}
          onView={setView}
          search={search}
          onSearch={setSearch}
        />
      ) : (
        <h1 className="text-3xl font-bold">{TITLE[section]}</h1>
      )}

      {active.isPending ? (
        <div className="space-y-2">
          {Array.from({ length: 8 }).map((_, i) => (
            <Skeleton key={i} className="h-14 w-full" />
          ))}
        </div>
      ) : active.items.length === 0 ? (
        <EmptyState
          variant="immersive"
          icon={<ListMusic className="size-8" />}
          title="موردی پیدا نشد"
        />
      ) : (
        <>
          {section === "songs" && (
            <SongList
              songs={songs.items}
              view={view}
              contextLabel={artistName}
              contextHref={artistPath({ id: artistId, code: artistCode })}
            />
          )}
          {section === "videos" && (
            <CardGrid columns="video">
              {videos.items.map((video) => (
                <VideoCard
                  key={video.id}
                  video={video}
                  contextLabel={`نماهنگ‌های ${artistName}`}
                  contextHref={artistPath({ id: artistId, code: artistCode }, "/videos")}
                />
              ))}
            </CardGrid>
          )}
          {section === "featuring" && (
            <CardGrid>
              {playlists.items.map((playlist) => (
                <MediaCard
                  key={playlist.id}
                  href={`/playlists/${playlist.id}`}
                  image={playlist.cover}
                  title={playlist.name}
                  subtitle={`${formatNumber(playlist.songCount, "fa")} آهنگ`}
                  fallbackIcon={<ListMusic className="size-8" />}
                />
              ))}
            </CardGrid>
          )}
          <InfiniteSentinel
            hasNext={!!active.hasNextPage}
            loading={active.isFetchingNextPage}
            onLoadMore={() => void active.fetchNextPage()}
          />
        </>
      )}
    </div>
  );
}
